import { Directive, effect, ElementRef, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';
import { map, switchMap } from 'rxjs';

@Directive({
  selector: '[appErrorStateHighlight]',
  standalone: true,
})
export class ErrorStateHighlightDirective {
  public errorStateControl = input.required<AbstractControl>();

  public errorStateClass = input('border-danger');

  public elementRef =
    inject<ElementRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>>(ElementRef);

  protected controlState = toSignal(
    toObservable(this.errorStateControl).pipe(
      switchMap((control) =>
        control.statusChanges.pipe(
          map(() => ({
            valid: control.valid,
            touched: control.touched,
            dirty: control.dirty,
            pristine: control.pristine,
          }))
        )
      )
    ),
    {
      initialValue: { valid: true, touched: false, dirty: false, pristine: true },
    }
  );

  constructor() {
    effect(() => {
      // Trigger the effect when control state changes (via signal)
      // but read the actual control state directly for accuracy
      this.controlState(); // This ensures the effect re-runs on state changes
      const control = this.errorStateControl();
      const isValid = control.valid;
      const isDirty = control.dirty;

      this.elementRef.nativeElement.classList.remove(this.errorStateClass());

      // Only highlight if the control is invalid AND dirty
      // Valid fields should never be highlighted, even if dirty
      if (!isValid && isDirty) {
        this.elementRef.nativeElement.classList.add(this.errorStateClass());
      }
    });
  }
}
